import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { Header } from './components/ui/header-1';
import { AppSidebar } from './components/app-sidebar';
import { PortalPageHeader } from './components/portal-page-header';
import { SidebarProvider, SidebarInset } from './components/ui/sidebar';

// Pages
import Home from './pages/Home';
import Auth from './pages/Auth';
import PartnerTypeSelection from './pages/PartnerTypeSelection';
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/Dashboard';
import ContentManagement from './pages/ContentManagement';
import ContentUpload from './pages/ContentUpload';
import FileUpload from './pages/FileUpload';
import ContentUploads from './pages/ContentUploads';
import SeriesList from './pages/SeriesList';
import CreateSeries from './pages/CreateSeries';
import SeriesDetail from './pages/SeriesDetail';
import Campaigns from './pages/Campaigns';
import Collaborations from './pages/Collaborations';
import CreateCampaign from './pages/CreateCampaign';
import CampaignDetail from './pages/CampaignDetail';
import MovieList from './pages/MovieList';
import CreateMovie from './pages/CreateMovie';
import MovieDetail from './pages/MovieDetail';
import AssetManagement from './pages/AssetManagement';
import TheatreMarketplace from './pages/TheatreMarketplace';
import ContractManagement from './pages/ContractManagement';
import Analytics from './pages/Analytics';
import Revenue from './pages/Revenue';
import Billing from './pages/Billing';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import Team from './pages/Team';
import Notifications from './pages/Notifications';
import Productions from './pages/Productions';
import ProductionDetail from './pages/ProductionDetail';
import { PortalRealtimeProvider } from './contexts/PortalRealtimeContext';
import { NotificationBell } from './components/notification-bell';

// Shared layout for all protected publisher portal pages
function PortalLayout({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <ProtectedRoute requirePublisher>
      <SidebarProvider>
        <AppSidebar variant="inset" />
        <SidebarInset>
          <PortalPageHeader title={title} actions={<NotificationBell />} />
          <div className="flex flex-1 flex-col">
            <div className="@container/main flex flex-1 flex-col gap-2">
              <div className="flex flex-col gap-6 py-6 md:gap-8 md:py-8">
                <div className="px-4 lg:px-6">
                  {children}
                </div>
              </div>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </ProtectedRoute>
  );
}

function AppContent() {
  const location = useLocation();
  const hideNavbarPaths = ['/auth', '/onboarding', '/select-partner-type'];
  const shouldHideNavbar = hideNavbarPaths.some(path =>
    location.pathname.startsWith(path)
  );

  const publisherRoutes = ['/dashboard', '/content', '/collaborations', '/campaigns', '/theatre-marketplace', '/contracts', '/analytics', '/revenue', '/billing', '/team', '/notifications', '/settings', '/profile', '/productions'];
  const isPublisherRoute = publisherRoutes.some(route => location.pathname.startsWith(route));

  return (
    <div className='dark partner-portal min-h-screen'>
      {!shouldHideNavbar && !isPublisherRoute && <Header />}
      <Routes>
        {/* Public routes */}
        <Route path='/' element={<Home />} />
        <Route path='/auth' element={<Auth />} />

        {/* Onboarding flow */}
        <Route
          path='/select-partner-type'
          element={
            <ProtectedRoute requireAuth>
              <PartnerTypeSelection />
            </ProtectedRoute>
          }
        />
        <Route
          path='/onboarding'
          element={
            <ProtectedRoute requireAuth>
              <Onboarding />
            </ProtectedRoute>
          }
        />

        {/* Publisher portal */}
        <Route path='/dashboard'                element={<PortalLayout title="Dashboard"><Dashboard /></PortalLayout>} />
        <Route path='/content'                        element={<PortalLayout title="Content Library"><ContentManagement /></PortalLayout>} />
        <Route path='/content/new'                    element={<PortalLayout title="New Upload"><ContentUpload /></PortalLayout>} />
        <Route path='/content/upload/:uploadId'       element={<PortalLayout title="Upload File"><FileUpload /></PortalLayout>} />
        <Route path='/content/uploads'                element={<PortalLayout title="My Uploads"><ContentUploads /></PortalLayout>} />
        <Route path='/content/movies'                 element={<PortalLayout title="Movies"><MovieList /></PortalLayout>} />
        <Route path='/content/movies/new'             element={<PortalLayout title="New Title"><CreateMovie /></PortalLayout>} />
        <Route path='/content/movies/:movieId'        element={<PortalLayout title="Movie"><MovieDetail /></PortalLayout>} />
        <Route path='/content/assets'                 element={<PortalLayout title="Asset Pipeline"><AssetManagement /></PortalLayout>} />
        <Route path='/content/series'                 element={<PortalLayout title="TV Series"><SeriesList /></PortalLayout>} />
        <Route path='/content/series/new'             element={<PortalLayout title="Create Series"><CreateSeries /></PortalLayout>} />
        <Route path='/content/series/:seriesId'       element={<PortalLayout title="Series Detail"><SeriesDetail /></PortalLayout>} />
        <Route path='/collaborations'                 element={<PortalLayout title="Collaborations"><Collaborations /></PortalLayout>} />
        <Route path='/campaigns'                      element={<PortalLayout title="Campaigns"><Campaigns /></PortalLayout>} />
        <Route path='/campaigns/new'                  element={<PortalLayout title="New Campaign"><CreateCampaign /></PortalLayout>} />
        <Route path='/campaigns/:campaignId'          element={<PortalLayout title="Campaign"><CampaignDetail /></PortalLayout>} />
        <Route path='/theatre-marketplace'            element={<PortalLayout title="Theatre Marketplace"><TheatreMarketplace /></PortalLayout>} />
        <Route path='/contracts'                element={<PortalLayout title="Contract Management"><ContractManagement /></PortalLayout>} />
        <Route path='/analytics'                element={<PortalLayout title="Analytics"><Analytics /></PortalLayout>} />
        <Route path='/revenue'                  element={<PortalLayout title="Revenue"><Revenue /></PortalLayout>} />
        <Route path='/billing'                  element={<PortalLayout title="Billing"><Billing /></PortalLayout>} />
        <Route path='/productions'               element={<PortalLayout title="Productions"><Productions /></PortalLayout>} />
        <Route path='/productions/:id'           element={<PortalLayout title="Production"><ProductionDetail /></PortalLayout>} />
        <Route path='/team'                     element={<PortalLayout title="Team"><Team /></PortalLayout>} />
        <Route path='/notifications'            element={<PortalLayout title="Notifications"><Notifications /></PortalLayout>} />
        <Route path='/settings'                 element={<PortalLayout title="Settings"><Settings /></PortalLayout>} />
        <Route path='/profile'                  element={<PortalLayout title="Profile"><Profile /></PortalLayout>} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <PortalRealtimeProvider>
          <AppContent />
          <Toaster />
        </PortalRealtimeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
